import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { useControls, folder } from 'leva'

import { cfgCassa } from '../../config/cassa'
import { DEBUG_MODE } from '../../config/debug'
import { cassaStore, cartStore, vicinoCassaStore, useStore, relockPointer, richiamoCassa } from '../../store/uiStore'
import { formatPrice } from '../../hooks/useCart'
import { IS_TOUCH } from '../../config/touch'
import { ndcSottoLaLente } from '../../config/lente'
import { CHIAVE_PREMI } from '../../config/gumball'

// ============================================================
// Lo scontrino che esce dal POS sul bancone.
//
// È un piano con una CanvasTexture, come il quadrante dell'orologio:
// il testo è disegnato su canvas e la carta "esce" facendo crescere il
// piano verso il basso mentre si scorre la texture, invece di scalare
// il disegno — altrimenti le scritte si allungherebbero uscendo.
//
// Fasi (le stesse dell'esperimento di riferimento): attesa → stampa →
// fatto. Le fa avanzare Hud, qui si guarda solo `cassaStore`.
// ============================================================

const LARG = 384                 // risoluzione della carta, in pixel
const MARGINE = 30
const RIGA = 30                  // interlinea del corpo
const DENTI = 26                 // dentelli del bordo strappato
const PROF_DENTE = 7

// quanti secondi ci mette la carta a uscire tutta (la usa anche
// CassaOverlay per non mostrare il pannello a stampa in corso)
export const DURATA_STAMPA = 2.1

// AVANZAMENTO A SCATTI, non continuo: una stampante termica spinge la
// carta una riga per volta, ed è quel ticchettio a rendere la cosa
// riconoscibile (nell'esperimento di riferimento è la modalità
// "stepped"). Si quantizza la corsa in passi da PASSO_CARTA pixel di
// canvas e si insegue il gradino con una molla svelta.
const PASSO_CARTA = 26
const VEL_SCATTO = 26

// Il bordo non è fatto di linee: in WebGL restano di UN pixel qualunque
// linewidth si chieda, e sul bancone chiaro sparivano. Sono volumetti
// sottili, con lo spessore tarabile (cfgCassa.bordoSpess).

// Quanto dura il richiamo (vedi richiamoCassa in uiStore): 4 s bastano a
// farsi notare senza diventare un lampeggiante.
const RICHIAMO_S = 4

// scratch: nel loop non si alloca
const t = {
  slot: new THREE.Vector3(),
  qSlot: new THREE.Quaternion(),
  eul: new THREE.Euler(),
  camP: new THREE.Vector3(),
  camQ: new THREE.Quaternion(),
  avanti: new THREE.Vector3(),
  su: new THREE.Vector3(),
  mira: new THREE.Vector3(),
}

const soldi = (m) => (m ? formatPrice(m) : '')

// I premi vinti alla macchinetta (vedi CHIAVE_PREMI in config/gumball.js):
// il carrello Shopify sa solo il CODICE, il nome del premio sta nel browser.
function premiVinti() {
  try { return JSON.parse(localStorage.getItem(CHIAVE_PREMI)) || {} } catch { return {} }
}

// Gli sconti che Shopify ha GIA' CALCOLATO sul carrello, sommati per codice
// (o per titolo, per gli sconti automatici del negozio): a livello di
// carrello o di riga, a seconda del tipo di sconto — si guardano entrambi.
function scontiCalcolati(cart, lines) {
  const mappa = new Map()
  const somma = (a) => {
    const chiave = a.code || a.title || 'sconto'
    const cur = mappa.get(chiave) || { code: a.code, title: a.title, importo: 0, valuta: a.discountedAmount?.currencyCode }
    cur.importo += Number(a.discountedAmount?.amount || 0)
    mappa.set(chiave, cur)
  }
  ;(cart?.discountAllocations || []).forEach(somma)
  lines.forEach((l) => (l.discountAllocations || []).forEach(somma))
  return mappa
}
const meno = (s) => '-' + formatPrice({ amount: s.importo, currencyCode: s.valuta || 'EUR' })

// Testo a due colonne: etichetta a sinistra, importo a destra
function riga(ctx, sinistra, destra, y, largh) {
  ctx.textAlign = 'left'
  ctx.fillText(sinistra, MARGINE, y)
  if (destra) {
    ctx.textAlign = 'right'
    ctx.fillText(destra, largh - MARGINE, y)
  }
}

function tratteggio(ctx, y, largh) {
  ctx.save()
  ctx.strokeStyle = 'rgba(0,0,0,0.35)'
  ctx.lineWidth = 2
  ctx.setLineDash([7, 7])
  ctx.beginPath()
  ctx.moveTo(MARGINE, y)
  ctx.lineTo(largh - MARGINE, y)
  ctx.stroke()
  ctx.restore()
}

// Costruisce l'elenco delle righe PRIMA di disegnare: serve a sapere
// quanto sarà lunga la carta, e quindi le proporzioni del piano.
function componi(lines, cart) {
  const voci = []
  voci.push({ t: 'titolo', s: 'MOMOZ SHOP' })
  voci.push({ t: 'piccolo', s: 'Via del Negozio 1 — Italia' })
  voci.push({ t: 'vuoto' })
  voci.push({ t: 'tratteggio' })
  voci.push({ t: 'piccolo', s: 'RIEPILOGO ORDINE' })
  voci.push({ t: 'tratteggio' })

  const sconti = scontiCalcolati(cart, lines)
  const codici = cart?.discountCodes || []
  const scontato = [...sconti.values()].some((s) => s.importo > 0)

  // Con uno sconto calcolato le righe si stampano a prezzo PIENO e lo sconto
  // sta su una riga sua: se una riga fosse gia' scontata e lo sconto
  // comparisse anche sotto, sembrerebbe tolto due volte.
  lines.forEach((l) => {
    const v = l.merchandise
    const opzioni = (v.selectedOptions || [])
      .filter((o) => o.value !== 'Default Title')
      .map((o) => o.value)
      .join(' · ')
    const prezzo = scontato ? (l.cost?.subtotalAmount || l.cost?.totalAmount) : l.cost?.totalAmount
    voci.push({ t: 'voce', s: v.product.title, d: soldi(prezzo) })
    voci.push({ t: 'piccolo', s: `  ${l.quantity} ×${opzioni ? '  ' + opzioni : ''}` })
  })

  // I PREMI DELLA MACCHINETTA E GLI SCONTI DEL NEGOZIO. Un codice che Shopify
  // ha applicato porta il suo importo; la spedizione gratis si calcola solo al
  // pagamento (serve l'indirizzo), e un codice che non vale piu' (scaduto,
  // gia' usato) lo si dice invece di far credere che scattera'.
  if (codici.length > 0 || sconti.size > 0) {
    const premi = premiVinti()
    voci.push({ t: 'tratteggio' })
    codici.forEach(({ code, applicable }) => {
      const premio = premi[code]
      const s = sconti.get(code)
      const spedizione = premio?.id === 'sped'
      voci.push({ t: 'voce', s: premio?.titolo || 'Codice sconto', d: s?.importo > 0 ? meno(s) : '' })
      const stato = s?.importo > 0 ? '' : spedizione || applicable ? ', al pagamento' : ', non valido'
      voci.push({ t: 'piccolo', s: `  ${code}${stato}` })
    })
    sconti.forEach((s) => {
      if (s.code || !(s.importo > 0)) return // i codici sono gia' sopra
      voci.push({ t: 'voce', s: s.title || 'Sconto', d: meno(s) })
    })
  }

  voci.push({ t: 'tratteggio' })
  // con uno sconto gia' calcolato il totale e' quello di Shopify, scontato
  voci.push({ t: 'totale', s: 'TOTALE', d: soldi(scontato ? cart?.cost?.totalAmount : cart?.cost?.subtotalAmount) })
  voci.push({ t: 'piccolo', s: codici.length > 0 || scontato ? 'Spedizione al pagamento' : 'Spedizione e sconti al pagamento' })
  voci.push({ t: 'vuoto' })
  voci.push({ t: 'tratteggio' })
  voci.push({ t: 'centro', s: 'GRAZIE E A PRESTO' })
  voci.push({ t: 'vuoto' })
  // Il pagamento è STAMPATO SULLA CARTA, non in un pannello a parte: il
  // totale era già qui sopra, e un riquadro fluttuante che lo ripeteva
  // sembrava un pezzo di un'altra interfaccia appoggiato sullo schermo.
  voci.push({ t: 'tratteggio' })
  voci.push({ t: 'tasto', s: 'PAGA ORA' })
  voci.push({ t: 'chiave', s: IS_TOUCH ? 'tocca il foglio per pagare' : 'clic sul foglio  ·  ESC per non pagare' })
  voci.push({ t: 'vuoto' })
  return voci
}

const altezzaDi = (v) =>
  v.t === 'titolo' ? 52 : v.t === 'totale' ? 44 : v.t === 'vuoto' ? 16
  : v.t === 'tratteggio' ? 20 : v.t === 'piccolo' ? 24
  : v.t === 'tasto' ? 76 : v.t === 'chiave' ? 26 : RIGA

// rettangolo del tagliando in pixel di canvas, riempito da disegna() e
// letto dal raycast: è l'unico punto cliccabile del foglio
const tasto = { x0: 0, y0: 0, x1: 0, y1: 0 }

function riquadro(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function disegna(canvas, voci, codice, acceso) {
  const alt = voci.reduce((s, v) => s + altezzaDi(v), 0) + MARGINE * 2 + 40
  canvas.width = LARG
  canvas.height = Math.round(alt)
  const ctx = canvas.getContext('2d')
  const H = canvas.height

  ctx.clearRect(0, 0, LARG, H)

  // sagoma della carta: bordo inferiore strappato
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(LARG, 0)
  ctx.lineTo(LARG, H - PROF_DENTE)
  for (let i = DENTI * 2 - 1; i >= 0; i--) {
    ctx.lineTo((LARG * i) / (DENTI * 2), i % 2 === 0 ? H : H - PROF_DENTE)
  }
  ctx.closePath()
  ctx.clip()

  ctx.fillStyle = '#f4f1e8'
  ctx.fillRect(0, 0, LARG, H)
  // ombreggiatura ai lati, per non farla sembrare un adesivo piatto
  const bordo = ctx.createLinearGradient(0, 0, LARG, 0)
  bordo.addColorStop(0, 'rgba(0,0,0,0.13)')
  bordo.addColorStop(0.12, 'rgba(0,0,0,0)')
  bordo.addColorStop(0.88, 'rgba(0,0,0,0)')
  bordo.addColorStop(1, 'rgba(0,0,0,0.13)')
  ctx.fillStyle = bordo
  ctx.fillRect(0, 0, LARG, H)

  ctx.fillStyle = '#1a1712'
  ctx.textBaseline = 'middle'
  let y = MARGINE + 10

  voci.forEach((v) => {
    const h = altezzaDi(v)
    const centro = y + h / 2
    if (v.t === 'tratteggio') {
      tratteggio(ctx, centro, LARG)
    } else if (v.t === 'titolo') {
      ctx.font = 'bold 34px "Courier New", Courier, monospace'
      ctx.textAlign = 'center'
      ctx.fillText(v.s, LARG / 2, centro)
    } else if (v.t === 'centro') {
      ctx.font = '20px "Courier New", Courier, monospace'
      ctx.textAlign = 'center'
      ctx.fillText(v.s, LARG / 2, centro)
    } else if (v.t === 'piccolo') {
      ctx.font = '17px "Courier New", Courier, monospace'
      ctx.fillStyle = 'rgba(26,23,18,0.62)'
      riga(ctx, v.s, v.d, centro, LARG)
      ctx.fillStyle = '#1a1712'
    } else if (v.t === 'totale') {
      ctx.font = 'bold 27px "Courier New", Courier, monospace'
      riga(ctx, v.s, v.d, centro, LARG)
    } else if (v.t === 'tasto') {
      const w = LARG - MARGINE * 2
      const h = altezzaDi(v) - 18
      const x = MARGINE
      const yr = y + 9
      tasto.x0 = x; tasto.y0 = yr; tasto.x1 = x + w; tasto.y1 = yr + h
      riquadro(ctx, x, yr, w, h, 10)
      ctx.fillStyle = acceso ? '#1a1712' : 'rgba(26,23,18,0.08)'
      ctx.fill()
      ctx.lineWidth = 2
      ctx.strokeStyle = 'rgba(26,23,18,0.75)'
      ctx.stroke()
      ctx.font = 'bold 27px "Courier New", Courier, monospace'
      ctx.textAlign = 'center'
      ctx.fillStyle = acceso ? '#f4f1e8' : '#1a1712'
      ctx.fillText(v.s, LARG / 2, yr + h / 2)
      ctx.fillStyle = '#1a1712'
    } else if (v.t === 'chiave') {
      ctx.font = '15px "Courier New", Courier, monospace'
      ctx.fillStyle = 'rgba(26,23,18,0.5)'
      ctx.textAlign = 'center'
      ctx.fillText(v.s, LARG / 2, centro)
      ctx.fillStyle = '#1a1712'
    } else if (v.t === 'voce') {
      ctx.font = '21px "Courier New", Courier, monospace'
      riga(ctx, v.s.length > 20 ? v.s.slice(0, 19) + '…' : v.s, v.d, centro, LARG)
    }
    y += h
  })

  ctx.font = '15px "Courier New", Courier, monospace'
  ctx.fillStyle = 'rgba(26,23,18,0.5)'
  ctx.textAlign = 'center'
  ctx.fillText(codice, LARG / 2, H - MARGINE)

  ctx.restore()
  return H
}

// Il contorno della faccia superiore del POS: un rettangolo arrotondato,
// un SOLO piano. Non è una linea (in WebGL resterebbe di un pixel) ma una
// striscia di triangoli fra due rettangoli concentrici.
//
// I due bordi si campionano con la STESSA struttura di angoli, così i
// punti si corrispondono uno a uno e la striscia si chiude da sola: il
// rettangolo interno ha lati e raggio ridotti dello stesso spessore,
// quindi i centri dei quattro archi coincidono con quelli esterni e i
// punti risultano allineati sul raggio.
const SEG_ANGOLO = 8

function contorno(semiL, semiP, r) {
  const p = []
  const centri = [
    [semiL - r, semiP - r, 0],
    [-(semiL - r), semiP - r, Math.PI / 2],
    [-(semiL - r), -(semiP - r), Math.PI],
    [semiL - r, -(semiP - r), -Math.PI / 2],
  ]
  for (const [cx, cy, a0] of centri) {
    for (let k = 0; k <= SEG_ANGOLO; k++) {
      const a = a0 + (k / SEG_ANGOLO) * (Math.PI / 2)
      p.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r)
    }
  }
  return p
}

function geometriaBordo(L, P, raggio, spessore) {
  const semiL = Math.max(L, 0.02) / 2
  const semiP = Math.max(P, 0.02) / 2
  const sp = Math.min(spessore, Math.min(semiL, semiP) * 0.8)
  // il raggio non può superare il lato corto, né restare negativo dentro
  const r = Math.min(Math.max(raggio, sp), Math.min(semiL, semiP))
  const fuori = contorno(semiL, semiP, r)
  const dentro = contorno(semiL - sp, semiP - sp, Math.max(r - sp, 0.0005))

  const n = fuori.length / 2
  const pos = new Float32Array(n * 6 * 3)
  let o = 0
  const metti = (arr, k) => {
    pos[o++] = arr[k * 2]      // x
    pos[o++] = 0               // il piano nasce già disteso in orizzontale
    pos[o++] = -arr[k * 2 + 1] // y del profilo → z del mondo
  }
  for (let k = 0; k < n; k++) {
    const k2 = (k + 1) % n
    metti(fuori, k); metti(dentro, k); metti(fuori, k2)
    metti(fuori, k2); metti(dentro, k); metti(dentro, k2)
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3))
  return g
}

export default function Cassa() {
  const fase = useStore(cassaStore)
  const cart = useStore(cartStore)
  const gruppo = useRef()
  const foglio = useRef()
  const avanz = useRef(0)
  const altezzaCanvas = useRef(1)
  const bordoRef = useRef()
  const vicino = useStore(vicinoCassaStore)
  const corsa = useRef(0)
  const lumin = useRef(0)
  const mostra = useRef(0)
  // CONGEDO: come sparisce il foglio quando si rinuncia a pagare.
  // Prima si limitava a tornare indietro lungo la stessa strada:
  // la carta si riavvolgeva e il gruppo rientrava alla bocchetta, cioè
  // DENTRO la mesh del bancone, e si vedeva benissimo. Ora la posa
  // resta quella dov'era, e il foglio si allontana sfumando.
  const congedo = useRef(0)
  const fasePrec = useRef(fase)
  useEffect(() => {
    if (fasePrec.current !== 'ferma' && fase === 'ferma' && avanz.current > 0.02) {
      congedo.current = 1
    }
    fasePrec.current = fase
  }, [fase])


  // Due cartelle separate, perché sono due oggetti distinti che prima si
  // confondevano in un elenco unico: la CARTA che esce e il CONTORNO che
  // si accende attorno al POS. I valori tornano comunque piatti — le
  // cartelle di Leva raggruppano solo l'interfaccia.
  const cfg = useControls('Cassa', {
    raggio: { value: cfgCassa.raggio, min: 0.5, max: 8, step: 0.1, label: 'distanza per pagare' },
    mostraSempre: { value: cfgCassa.mostraSempre, label: 'mostra sempre (taratura)' },

    'Scontrino (la carta)': folder({
      x: { value: cfgCassa.x, min: -12, max: 12, step: 0.01, label: 'bocchetta X' },
      y: { value: cfgCassa.y, min: 0, max: 5, step: 0.01, label: 'bocchetta Y' },
      z: { value: cfgCassa.z, min: -12, max: 12, step: 0.01, label: 'bocchetta Z' },
      giro: { value: cfgCassa.giro, min: -180, max: 180, step: 1, label: 'verso dove punta' },
      inclina: { value: cfgCassa.inclina, min: -90, max: 90, step: 1, label: 'muso su/giù' },
      rolla: { value: cfgCassa.rolla, min: -90, max: 90, step: 1, label: 'sbandata' },
      piega: { value: cfgCassa.piega, min: -40, max: 95, step: 1, label: 'piega della carta' },
      larghezza: { value: cfgCassa.larghezza, min: 0.05, max: 0.5, step: 0.005, label: 'larghezza carta' },
      zoomDist: { value: cfgCassa.zoomDist, min: 0.2, max: 1.2, step: 0.01, label: 'fine: distanza occhi' },
      zoomSu: { value: cfgCassa.zoomSu, min: -0.3, max: 0.3, step: 0.01, label: 'fine: altezza' },
    }, { collapsed: true }),

    'Contorno (il bordo bianco)': folder({
      bordoX: { value: cfgCassa.bordoX, min: -12, max: 12, step: 0.01, label: 'posizione X' },
      bordoY: { value: cfgCassa.bordoY, min: 0, max: 5, step: 0.01, label: 'posizione Y' },
      bordoZ: { value: cfgCassa.bordoZ, min: -12, max: 12, step: 0.01, label: 'posizione Z' },
      boxL: { value: cfgCassa.boxL, min: 0.05, max: 2, step: 0.01, label: 'larghezza' },
      boxP: { value: cfgCassa.boxP, min: 0.05, max: 2, step: 0.01, label: 'profondità' },
      bordoGiro: { value: cfgCassa.bordoGiro, min: -180, max: 180, step: 1, label: 'giro nel piano' },
      bordoInclina: { value: cfgCassa.bordoInclina, min: -90, max: 90, step: 1, label: 'pendenza avanti/indietro' },
      bordoRolla: { value: cfgCassa.bordoRolla, min: -90, max: 90, step: 1, label: 'pendenza laterale' },
      bordoRaggio: { value: cfgCassa.bordoRaggio, min: 0, max: 0.3, step: 0.005, label: 'angoli' },
      bordoSpess: { value: cfgCassa.bordoSpess, min: 0.001, max: 0.03, step: 0.001, label: 'spessore del filo' },
    }, { collapsed: true }),
  }, { collapsed: true })
  // Si applica SOLO quando uno slider si muove davvero. Riassegnando a
  // ogni render, un valore scritto a mano su window.__cassa durerebbe un
  // frame — e la taratura da console (o da uno script) sarebbe inutile.
  const cfgPrec = useRef(null)
  if (!cfgPrec.current || Object.keys(cfg).some((k) => cfg[k] !== cfgPrec.current[k])) {
    Object.assign(cfgCassa, cfg)
    cfgPrec.current = { ...cfg }
  }

  // In ?debug: window.__mira() dice su cosa e DOVE sta guardando il
  // mirino. Serve a trovare la fessura del POS, che essendo cotta nella
  // mesh del negozio non ha coordinate leggibili da nessuna parte.
  const raggio = useThree((st) => st.raycaster)
  const scena = useThree((st) => st.scene)
  const camera3d = useThree((st) => st.camera)
  useEffect(() => {
    if (!DEBUG_MODE) return undefined
    const centro = new THREE.Vector2(0, 0)
    const miaRoba = (ob) => {
      for (let n = ob; n; n = n.parent) {
        if (n.userData?.noRay || n.name === 'Scontrino' || n.name === 'OrologioFaccia') return true
      }
      return false
    }
    window.__mira = () => {
      raggio.setFromCamera(centro, camera3d)
      const urto = raggio.intersectObjects(scena.children, true)
        .find((u) => u.object.visible && !miaRoba(u.object))
      if (!urto) return null
      const p = urto.point
      return {
        x: +p.x.toFixed(3), y: +p.y.toFixed(3), z: +p.z.toFixed(3),
        distanza: +urto.distance.toFixed(2),
        oggetto: urto.object.name || urto.object.type,
      }
    }
    // window.__sonda(x, z): guarda IN GIU' da sopra il soffitto e dice cosa
    // c'e' a quell'x/z e a che altezza. Serve a trovare un posto libero sul
    // bancone per appoggiarci qualcosa: il bancone e' cotto nella mesh del
    // negozio insieme a POS, monitor e soprammobili, e i collisori dicono
    // solo dov'e' il piano, non cosa ci sta sopra.
    const giu = new THREE.Vector3(0, -1, 0)
    const da = new THREE.Vector3()
    // Parte da `quota` (4.5 di default): SOTTO il soffitto, che sta a ~6.5 e
    // altrimenti e' la prima cosa che si colpisce, ma sopra qualunque
    // soprammobile del bancone.
    window.__sonda = (x, z, quota = 4.5) => {
      raggio.set(da.set(x, quota, z), giu)
      const urto = raggio.intersectObjects(scena.children, true)
        .find((u) => u.object.visible && !miaRoba(u.object))
      return urto ? { y: +urto.point.y.toFixed(3), oggetto: urto.object.name || urto.object.type } : null
    }
    return () => { delete window.__mira; delete window.__sonda }
  }, [raggio, scena, camera3d])

  // si ricostruisce solo quando si muovono gli slider delle misure
  const geoBordo = useMemo(
    () => geometriaBordo(cfg.boxL, cfg.boxP, cfg.bordoRaggio, cfg.bordoSpess),
    [cfg.boxL, cfg.boxP, cfg.bordoRaggio, cfg.bordoSpess]
  )
  useEffect(() => () => geoBordo.dispose(), [geoBordo])

  const canvas = useMemo(() => document.createElement('canvas'), [])
  const texture = useMemo(() => {
    const t = new THREE.CanvasTexture(canvas)
    t.colorSpace = THREE.SRGBColorSpace
    t.anisotropy = 4
    return t
  }, [canvas])
  useEffect(() => () => texture.dispose(), [texture])

  // Il codice d'ordine è finto: l'ordine vero lo crea Shopify al
  // pagamento, serve solo a far sembrare lo scontrino uno scontrino.
  // Si estrae all'inizio della stampa e NON deve cambiare durante, per
  // questo sta in un ref e non in un memo legato alla fase.
  const codice = useRef('')
  useEffect(() => {
    if (fase === 'attesa') {
      // "RIF." e non "MOMOZ-": MOMOZ- e' il prefisso dei codici sconto della
      // macchinetta, stampati poco sopra, e questo riferimento finto non
      // deve sembrare un codice da provare al checkout
      codice.current = 'RIF. ' + Math.random().toString(36).slice(2, 7).toUpperCase()
    }
  }, [fase])

  // le voci restano a portata di mano: il tagliando si riaccende al
  // passaggio del mouse, e ridisegnarlo vuol dire ridisegnare la carta
  const voci = useRef([])
  const acceso = useRef(false)

  // si ridisegna quando cambia il carrello o quando parte una stampa
  useEffect(() => {
    // in taratura si disegna comunque, altrimenti la carta resterebbe
    // un piano alto un pixel con sopra una texture vuota
    if (fase === 'ferma' && !cfg.mostraSempre) return
    const lines = cart?.lines?.edges?.map((e) => e.node) || []
    voci.current = componi(lines, cart)
    acceso.current = false
    altezzaCanvas.current = disegna(canvas, voci.current, codice.current, false)
    texture.needsUpdate = true
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
  }, [fase, cart, canvas, texture, cfg.mostraSempre])

  // ------------------------------------------------------------------
  // IL PAGAMENTO SI PREME SULLA CARTA
  // Niente pannello a parte: il tagliando in fondo allo scontrino è
  // l'unica zona sensibile. Si mira col mouse (il puntatore è libero:
  // il pointer lock viene rilasciato appena la stampa finisce) e si
  // converte il punto colpito in pixel di canvas passando per la uv.
  // ------------------------------------------------------------------
  const dom = useThree((st) => st.gl.domElement)
  const cartRif = useRef(cart)
  cartRif.current = cart
  useEffect(() => {
    if (fase !== 'fatta') return undefined
    const ndc = new THREE.Vector2()
    const lente = new THREE.Vector2()
    const rc = new THREE.Raycaster()

    // ritorna true se il punto dello schermo cade sul tagliando
    const sulTasto = (ev) => {
      const f = foglio.current
      if (!f || !f.visible) return false
      const r = dom.getBoundingClientRect()
      ndc.set(
        ((ev.clientX - r.left) / r.width) * 2 - 1,
        -((ev.clientY - r.top) / r.height) * 2 + 1
      )
      // Lo schermo mostra l'immagine passata sotto la lente a barile
      // (Effects.jsx): senza rifare quel conto si mira il disegno
      // deformato e si colpisce la scena non deformata — il tagliando
      // risultava più stretto e più in alto di come appare.
      ndcSottoLaLente(ndc.x, ndc.y, r.width / r.height, lente)
      rc.setFromCamera(lente, camera3d)
      const urto = rc.intersectObject(f, false)[0]
      if (!urto || !urto.uv) return false
      // a stampa finita la texture non è più scorrata (repeat 1, offset 0),
      // quindi la uv del piano è già la coordinata del disegno
      const px = urto.uv.x * LARG
      const py = (1 - urto.uv.y) * canvas.height
      return px >= tasto.x0 && px <= tasto.x1 && py >= tasto.y0 && py <= tasto.y1
    }

    const suMossa = (ev) => {
      const sopra = sulTasto(ev)
      if (sopra === acceso.current) return
      acceso.current = sopra
      disegna(canvas, voci.current, codice.current, sopra)
      texture.needsUpdate = true
      dom.style.cursor = sopra ? 'pointer' : ''
    }

    const suClic = (ev) => {
      if (!sulTasto(ev)) return
      const url = cartRif.current?.checkoutUrl
      if (url) window.location.href = url
    }

    const suTasto = (ev) => {
      if (ev.code !== 'Escape' && ev.key !== 'Escape') return
      cassaStore.set('ferma')
      relockPointer()
    }

    window.addEventListener('pointermove', suMossa)
    window.addEventListener('pointerdown', suClic)
    window.addEventListener('keydown', suTasto)
    return () => {
      window.removeEventListener('pointermove', suMossa)
      window.removeEventListener('pointerdown', suClic)
      window.removeEventListener('keydown', suTasto)
      dom.style.cursor = ''
    }
  }, [fase, dom, camera3d, canvas, texture])

  useFrame((state, delta) => {
    const g = gruppo.current
    const f = foglio.current
    if (!g || !f) return

    // --- dove sta il gruppo: alla cassa, oppure davanti agli occhi ---
    // A stampa finita lo scontrino viene portato davanti alla visuale
    // per poterlo leggere: si interpola fra la posa alla bocchetta e una
    // posa agganciata alla camera. Prendendo la rotazione della camera,
    // il foglio le sta di fronte da solo — il +Z di una camera punta
    // all'indietro, cioè verso chi guarda.
    // durante il congedo la posa NON si aggiorna: il foglio resta dove
    // il giocatore lo stava guardando invece di rifare la strada al
    // contrario e infilarsi nel bancone
    if (congedo.current === 0) {
      mostra.current += ((fase === 'fatta' ? 1 : 0) - mostra.current) *
                        (1 - Math.exp(-delta * 6))
      if (mostra.current < 0.002) mostra.current = 0
    }

    t.slot.set(cfgCassa.x, cfgCassa.y, cfgCassa.z)
    const RAD = Math.PI / 180
    t.qSlot.setFromEuler(t.eul.set(
      cfgCassa.inclina * RAD, cfgCassa.giro * RAD, cfgCassa.rolla * RAD, 'YXZ'
    ))
    if (mostra.current > 0) {
      state.camera.getWorldPosition(t.camP)
      state.camera.getWorldQuaternion(t.camQ)
      t.avanti.set(0, 0, -1).applyQuaternion(t.camQ)
      t.su.set(0, 1, 0).applyQuaternion(t.camQ)
      t.mira.copy(t.camP)
        .addScaledVector(t.avanti, cfgCassa.zoomDist)
        .addScaledVector(t.su, cfgCassa.zoomSu)
      g.position.lerpVectors(t.slot, t.mira, mostra.current)
      g.quaternion.copy(t.qSlot).slerp(t.camQ, mostra.current)
    } else {
      g.position.copy(t.slot)
      g.quaternion.copy(t.qSlot)
    }

    // Il congedo aggiunge lo scostamento: il foglio si allontana da chi
    // guarda e scende un poco, come posato. Le direzioni si prendono
    // dalla camera anche quando la posa è quella della bocchetta —
    // altrimenti, rinunciando a stampa ancora in corso, il foglio si
    // scosterebbe in una direzione che con la visuale non c'entra.
    if (congedo.current > 0) {
      const via = 1 - congedo.current
      state.camera.getWorldQuaternion(t.camQ)
      t.avanti.set(0, 0, -1).applyQuaternion(t.camQ)
      t.su.set(0, 1, 0).applyQuaternion(t.camQ)
      g.position
        .addScaledVector(t.avanti, via * 0.10)
        .addScaledVector(t.su, -via * 0.07)
    }

    // --- riquadro luminoso attorno al POS ---
    // Vive fuori dal gruppo della carta e ha coordinate di mondo: prima
    // era un figlio, e ogni spostamento della bocchetta se lo portava
    // dietro. Sono due oggetti distinti sullo stesso mobile.
    const st = bordoRef.current
    if (st) {
      // 1 appena scatta il richiamo, 0 quando e' finito: e' lui a decidere
      // sia l'accensione (anche da lontano) sia il ritmo del battito
      const richiamo = Math.max(0,
        1 - (performance.now() - richiamoCassa.da) / (RICHIAMO_S * 1000))
      const acceso = cfgCassa.mostraSempre || richiamo > 0 ||
        (fase === 'ferma' && vicino && (cart?.totalQuantity || 0) > 0)
      lumin.current += ((acceso ? 1 : 0) - lumin.current) * (1 - Math.exp(-delta * 8))
      if (lumin.current < 0.004) lumin.current = 0
      st.visible = lumin.current > 0
      if (st.visible) {
        st.position.set(cfgCassa.bordoX, cfgCassa.bordoY, cfgCassa.bordoZ)
        // ORDINE YXZ, non il predefinito: il giro deve avvenire PRIMA,
        // perché è la rotazione del rettangolo nel proprio piano; poi si
        // inclina attorno all'asse già girato. Con l'ordine XYZ la
        // pendenza resterebbe agganciata all'asse X del mondo, e su un
        // POS storto il rettangolo si accartoccerebbe invece di
        // appoggiarsi alla faccia.
        st.rotation.set(
          cfgCassa.bordoInclina * (Math.PI / 180),
          cfgCassa.bordoGiro * (Math.PI / 180),
          cfgCassa.bordoRolla * (Math.PI / 180),
          'YXZ'
        )
        // DUE BATTITI: a riposo il contorno RESPIRA (lento e discreto, così
        // si distingue dai riflessi del negozio senza gridare); durante il
        // richiamo PULSA veloce e pieno, perché in quel momento è un dito
        // puntato. Nel richiamo la luce non aspetta la rampa di `lumin`:
        // si prende il massimo fra le due, così si accende sul colpo.
        const battito = richiamo > 0
          ? 0.72 + 0.28 * Math.sin(state.clock.elapsedTime * 11)
          : 0.45 + 0.15 * Math.sin(state.clock.elapsedTime * 2.4)
        st.material.opacity = Math.max(lumin.current, richiamo) * battito
        // Nel richiamo pulsa anche di DIMENSIONE: da lontano il filo e'
        // spesso due centimetri e la sola opacita' non si nota. Scala
        // uniforme, quindi la cornice resta della sua forma — allargando un
        // asse solo, lo spessore si stirerebbe in modo diverso sui due lati.
        st.scale.setScalar(richiamo > 0
          ? 1 + 0.07 * richiamo * (0.5 + 0.5 * Math.sin(state.clock.elapsedTime * 11))
          : 1)
      }
    }

    // --- avanzamento della carta ---
    if (congedo.current > 0) {
      // ~0.45 s: la carta si scosta di un palmo e svanisce
      congedo.current -= delta * 2.2
      if (congedo.current <= 0) {
        congedo.current = 0
        corsa.current = 0
        avanz.current = 0
        mostra.current = 0
        f.material.opacity = 1
      }
    } else if (fase === 'stampa') {
      corsa.current = Math.min(1, corsa.current + delta / DURATA_STAMPA)
      // Il gradino: la carta non scorre liscia, sale di una riga per
      // volta e fra un passo e l'altro sta ferma. È il ticchettio a
      // rendere riconoscibile una stampante termica.
      const passi = Math.max(1, Math.round(altezzaCanvas.current / PASSO_CARTA))
      const gradino = Math.min(1, Math.ceil(corsa.current * passi) / passi)
      avanz.current += (gradino - avanz.current) * (1 - Math.exp(-delta * VEL_SCATTO))
    } else if (fase === 'fatta') {
      corsa.current = 1
      avanz.current += (1 - avanz.current) * (1 - Math.exp(-delta * 12))
    } else if (cfgCassa.mostraSempre) {  // eslint-disable-line no-dupe-else-if
      // taratura: la carta resta tutta fuori alla bocchetta, ferma
      corsa.current = 1
      avanz.current += (1 - avanz.current) * (1 - Math.exp(-delta * 12))
    } else {
      corsa.current = 0
      avanz.current += (0 - avanz.current) * (1 - Math.exp(-delta * 7))
      if (avanz.current < 0.002) avanz.current = 0
    }

    const a = avanz.current
    f.visible = a > 0.004
    if (!f.visible) return
    // sfumata: è questa a far sparire il foglio, non il riavvolgimento
    f.material.opacity = congedo.current > 0 ? congedo.current * congedo.current : 1

    // Il piano cresce verso il basso e la texture scorre con lui: così
    // la carta ESCE invece di allungarsi.
    const lunghezza = cfgCassa.larghezza * (altezzaCanvas.current / LARG)
    const fuori = lunghezza * a
    f.scale.set(cfgCassa.larghezza, fuori, 1)
    // avvicinandosi agli occhi la carta smette di pendere e si centra
    const resta = 1 - mostra.current
    const piega = ((cfgCassa.piega * Math.PI) / 180) * resta
    f.rotation.set(piega, 0, 0)
    // micro-vibrazione del meccanismo, solo mentre stampa
    const vibra = fase === 'stampa'
      ? Math.sin(state.clock.elapsedTime * 53) * cfgCassa.larghezza * 0.008
      : 0
    f.position.set(
      vibra,
      -Math.cos(piega) * (fuori / 2) * resta,
      Math.sin(piega) * (fuori / 2) * resta
    )
    texture.repeat.set(1, a)
    texture.offset.set(0, 1 - a)
  })

  return (
    <>
      {/* filo bianco attorno alla faccia del POS: si accende avvicinandosi.
          Fuori dal gruppo della carta, con coordinate di mondo proprie. */}
      <mesh ref={bordoRef} geometry={geoBordo} visible={false} renderOrder={2}>
        <meshBasicMaterial
          /* lo stesso celeste del flusso "aggiunto al carrello"
             (config.colore in CartFlux.jsx): le due cose parlano dello
             stesso gesto, comprare, e devono sembrare parenti */
          color="#8fd8ff"
          side={THREE.DoubleSide}
          transparent
          opacity={0}
          depthTest={false}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      {/* la carta: sta nel gruppo, che a stampa finita si sposta
          davanti agli occhi portandosela dietro */}
      <group ref={gruppo}>
        <mesh ref={foglio} name="Scontrino" visible={false}>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial
            map={texture}
            transparent
            side={THREE.DoubleSide}
            toneMapped={false}
          />
        </mesh>
      </group>
    </>
  )
}
